(function () {
  "use strict";

  const DESKTOP_MEDIA_QUERY = window.matchMedia("(min-width:1150px)");

  // Lazy initialization flags
  let isInitialized = false;
  let initializationPromise = null;

  const SELECTORS = {
    PANEL: ".panel",
    PANEL_WRAPPER: ".panel__wrapper",
    PANEL_LIST_WRAPPER: ".panel-list__wrapper",
    FIRST_PANEL: ".panel:first-child",
    NON_FIRST_PANELS: ".panel:not(:first-child)",
    HEADER_BUTTONS: ".header__link-list button[data-target-section]",
    LEVEL_1_BUTTONS: '.panel:first-child [data-panel^="1-"]',
  };

  const LEVEL_CLASSES = ["show-level-2", "show-level-3", "show-level-4"];
  let listenersAdded = false;

  // DOM Cache System (only initialized when needed)
  const domCache = new WeakMap();

  function getDOMCache(drawer) {
    if (!domCache.has(drawer)) {
      const cache = {
        panels: drawer.querySelectorAll(SELECTORS.PANEL),
        panelWrapper: drawer.querySelector(SELECTORS.PANEL_LIST_WRAPPER),
        mobileLogo: drawer.querySelector(".navigation-header__mobile-logo"),
        backButton: drawer.querySelector(".navigation-header__back-button"),
        backButtonTitle: drawer.querySelector(
          ".navigation-header__back-button .navigation-header__title"
        ),
        desktopBackLink: drawer.querySelector(
          ".navigation-header__desktop-back-link"
        ),
        level1Buttons: drawer.querySelectorAll(SELECTORS.LEVEL_1_BUTTONS),
        panelWrappers: new Map(),
      };

      // Pre-cache panel wrappers for each panel
      cache.panels.forEach((panel, index) => {
        cache.panelWrappers.set(
          index,
          panel.querySelectorAll(SELECTORS.PANEL_WRAPPER)
        );
      });

      domCache.set(drawer, cache);
    }
    return domCache.get(drawer);
  }

  function clearDOMCache(drawer) {
    domCache.delete(drawer);
  }

  // Direct Level 2 functionality (available immediately)
  function showDirectLevel2Panel(drawer, matchingPanelIndex, navItemTitle) {
    const cache = getDOMCache(drawer);

    if (!cache.panels.length || !cache.panelWrapper) {
      return;
    }

    const panel0 = cache.panels[0];
    if (panel0) {
      panel0.style.display = "none";
    }

    for (let i = 2; i < cache.panels.length; i++) {
      const panel = cache.panels[i];
      if (panel) {
        panel.removeAttribute("open");
        panel.style.opacity = "0";
        panel.style.visibility = "hidden";
        const wrappers = cache.panelWrappers.get(i);
        if (wrappers) {
          wrappers.forEach((wrapper) => wrapper.setAttribute("hidden", ""));
        }
      }
    }

    const level2Panel = cache.panels[1];

    if (level2Panel && matchingPanelIndex !== null) {
      level2Panel.style.opacity = "1";
      level2Panel.style.visibility = "visible";
      level2Panel.setAttribute("open", "");

      const linkLists = cache.panelWrappers.get(1);

      if (linkLists && linkLists.length > 0 && drawer.switchLinkList) {
        drawer.switchLinkList(linkLists, matchingPanelIndex, true);
      } else if (linkLists && linkLists.length > 0) {
        linkLists.forEach((item, index) => {
          if (index === matchingPanelIndex) {
            item.removeAttribute("hidden");
          } else {
            item.setAttribute("hidden", "");
          }
        });
      }
    }

    cache.panelWrapper.classList.remove(...LEVEL_CLASSES);
    cache.panelWrapper.classList.add("show-level-2");
    drawer.classList.remove(...LEVEL_CLASSES);
    drawer.classList.add("show-level-2");

    drawer.classList.add("direct-level-2-mode");
    drawer._isDirectLevel2Mode = true;

    cache.panelWrapper.style.setProperty("--navigation-drawer-width", "412px");

    if (cache.desktopBackLink) {
      cache.desktopBackLink.style.display = "flex";
    }

    drawer._navigationPath = [`1-${matchingPanelIndex + 1}`];

    // updateNavigationPath will be available after full initialization
    if (typeof updateNavigationPath === "function") {
      updateNavigationPath(drawer, drawer._navigationPath);
    }
  }

  // Simplified NavigationDrawer availability check
  function waitForNavigationDrawer() {
    // If NavigationDrawer class is available, we're good
    if (typeof NavigationDrawer !== "undefined") {
      return Promise.resolve();
    }

    // If custom element is registered, we're also good
    if (window.customElements.get("navigation-drawer")) {
      return Promise.resolve();
    }

    // Otherwise wait for custom element definition (no timeout needed since we're loading lazily)
    return customElements.whenDefined("navigation-drawer");
  }

  // Lazy initialization function
  function lazyInitialize() {
    if (isInitialized) return Promise.resolve();
    if (initializationPromise) return initializationPromise;

    initializationPromise = waitForNavigationDrawer()
      .then(() => {
        // Load navigation images for all drawers when system initializes
        const drawers = document.querySelectorAll("navigation-drawer");
        drawers.forEach((drawer) => {
          if (drawer.hasAttribute("open")) {
            loadNavigationImages(drawer);
          }
        });

        return initializeNavigationSystem();
      })
      .then(() => {
        isInitialized = true;
      })
      .catch((error) => {
        console.error(
          "Failed to initialize 4-Level Navigation Extension:",
          error
        );
        isInitialized = false;
        initializationPromise = null;
      });

    return initializationPromise;
  }

  // Setup lightweight triggers that initialize the full system only when needed
  function setupLazyTriggers() {
    // Trigger on navigation button hover (desktop) - Most common first interaction
    const setupHoverTrigger = () => {
      if (!DESKTOP_MEDIA_QUERY.matches) return;

      const headerButtons = document.querySelectorAll(SELECTORS.HEADER_BUTTONS);
      headerButtons.forEach((button) => {
        if (!button.dataset.lazyTrigger) {
          button.addEventListener("mouseenter", lazyInitialize, {
            once: true,
            passive: true,
          });
          button.dataset.lazyTrigger = "true";
        }
      });
    };

    // Immediate Direct Level 2 handler for header buttons (no lazy loading)
    const setupDirectLevel2Handler = () => {
      if (!DESKTOP_MEDIA_QUERY.matches) return;

      const headerButtons = document.querySelectorAll(SELECTORS.HEADER_BUTTONS);
      headerButtons.forEach((button) => {
        if (!button.dataset.directLevel2Handler) {
          button.addEventListener("click", handleDirectLevel2Click, {
            capture: true,
          });
          button.dataset.directLevel2Handler = "true";
        }
      });
    };

    // Trigger on navigation drawer opening (mobile/desktop)
    const setupOpenTrigger = () => {
      const drawers = document.querySelectorAll("navigation-drawer");
      drawers.forEach((drawer) => {
        if (!drawer.dataset.lazyTrigger) {
          // Listen for the open attribute being added
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (
                mutation.attributeName === "open" &&
                drawer.hasAttribute("open")
              ) {
                lazyInitialize();
                observer.disconnect(); // Only trigger once
              }
            });
          });

          observer.observe(drawer, {
            attributes: true,
            attributeFilter: ["open"],
          });

          drawer.dataset.lazyTrigger = "true";
        }
      });
    };

    // Trigger on mobile menu button click
    const setupMobileMenuTrigger = () => {
      const mobileMenuButtons = document.querySelectorAll(
        '[aria-controls*="header-sidebar-menu"]'
      );
      mobileMenuButtons.forEach((button) => {
        if (!button.dataset.lazyTrigger) {
          button.addEventListener("click", lazyInitialize, {
            once: true,
            passive: true,
          });
          button.dataset.lazyTrigger = "true";
        }
      });
    };

    // Schedule setup after current execution stack
    requestAnimationFrame(() => {
      setupHoverTrigger();
      setupDirectLevel2Handler(); // Add direct level 2 handler immediately
      setupOpenTrigger();
      setupMobileMenuTrigger();
    });
  }

  // Direct Level 2 Click Handler (available immediately, no lazy loading)
  function handleDirectLevel2Click(event) {
    if (!DESKTOP_MEDIA_QUERY.matches) {
      return;
    }

    // Prevent default drawer opening behavior
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const drawerId = button.getAttribute("aria-controls");

    if (!drawerId) return;

    const drawer = document.getElementById(drawerId);
    if (!drawer) {
      return;
    }

    // Set flag to prevent navigation reset during direct level 2 opening
    drawer._directLevel2Opening = true;

    // Manually open the drawer first
    drawer.setAttribute("open", "");

    // Ensure lazy initialization happens for full navigation system
    lazyInitialize()
      .then(() => {
        // DOM cache is now available after initialization
        const cache = getDOMCache(drawer);

        if (!cache.level1Buttons.length) {
          return;
        }

        const clickedText = button.textContent.trim();

        let matchingPanelIndex = null;

        for (const [btnIndex, btn] of cache.level1Buttons.entries()) {
          if (btn.textContent.trim() === clickedText) {
            matchingPanelIndex = btnIndex;
            break;
          }
        }

        if (matchingPanelIndex !== null) {
          // Execute direct level 2 panel logic
          showDirectLevel2Panel(drawer, matchingPanelIndex, clickedText);

          // Clear the flag after direct level 2 is set up
          setTimeout(() => {
            drawer._directLevel2Opening = false;
          }, 500); // Increased from 50ms to 500ms to prevent DOM change resets
        } else {
          drawer._directLevel2Opening = false;
        }
      })
      .catch((error) => {
        console.error("❌ Lazy initialization failed:", error);
        drawer._directLevel2Opening = false;
      });
  }

  // Initialize lazy triggers immediately (minimal DOM impact)
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupLazyTriggers, {
      once: true,
      passive: true,
    });
  } else {
    setupLazyTriggers();
  }

  // Lazy load navigation images when drawer opens
  function loadNavigationImages(drawer) {
    const lazyImages = drawer.querySelectorAll(".lazy-nav-image[data-src]");

    lazyImages.forEach((img) => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute("data-src");
      }
    });
  }

  // Main initialization function (only called when navigation is actually used)
  function initializeNavigationSystem() {
    let hasNavigationDrawer = typeof NavigationDrawer !== "undefined";

    // Override NavigationDrawer's showPanel method with our optimized version
    if (hasNavigationDrawer) {
      NavigationDrawer.prototype.showPanel = function (
        panelIndex,
        linkListIndex = null
      ) {
        return customShowPanel(this, panelIndex, linkListIndex);
      };
    }

    function customShowPanel(
      drawer,
      panelIndex,
      linkListIndex = null,
      headerTitle = null
    ) {
      if (typeof panelIndex !== "number" || panelIndex < 0) return;

      const cache = getDOMCache(drawer);

      if (!cache.panels.length || !cache.panelWrapper) return;

      const panelToShow = cache.panels[panelIndex];
      if (!panelToShow) return;

      if (headerTitle) {
        updateNavigationHeader(drawer, headerTitle);
      }

      const linkLists = cache.panelWrappers.get(panelIndex);
      let timelineSequence = [];

      hidePanels(cache.panels, panelIndex + 1);

      if (drawer._isDirectLevel2Mode) {
        const adjustedLevel = panelIndex;
        setLevelClass(cache.panelWrapper, adjustedLevel);
        setLevelClass(drawer, adjustedLevel);

        const widths = {
          1: "412px",
          2: "792px",
          3: "1172px",
        };
        cache.panelWrapper.style.setProperty(
          "--navigation-drawer-width",
          widths[adjustedLevel] || "412px"
        );
      } else {
        setLevelClass(cache.panelWrapper, panelIndex);
        setLevelClass(drawer, panelIndex);
      }
      for (let i = panelIndex; i >= 0; i--) {
        const targetIndex = 3 - i;
        if (targetIndex < cache.panels.length && cache.panels[targetIndex]) {
          if (panelIndex < targetIndex) {
            cache.panels[targetIndex].style.opacity = "0";
            cache.panels[targetIndex].style.visibility = "hidden";
          }
        }
      }

      if (panelIndex === 0) {
        hidePanels(cache.panels, 1);
      }

      if (!drawer._isTransitioning) {
        drawer._isTransitioning = true;
        panelToShow.style.opacity = "1";
        panelToShow.style.visibility = "visible";

        if (
          linkListIndex !== null &&
          linkLists &&
          linkLists.length > 0 &&
          drawer.switchLinkList
        ) {
          drawer.switchLinkList(linkLists, linkListIndex, true);
        } else if (linkLists && linkLists.length > 0) {
          linkLists.forEach((item, index) => {
            if (index === 0) {
              item.removeAttribute("hidden");
            } else {
              item.setAttribute("hidden", "");
            }
          });
        }
      }

      if (
        drawer.previousLinkIndex !== undefined &&
        drawer.previousLinkIndex !== linkListIndex &&
        linkLists &&
        linkLists[drawer.previousLinkIndex]
      ) {
        timelineSequence.push([
          linkLists[drawer.previousLinkIndex],
          { opacity: [1, 0], visibility: ["visible", "hidden"] },
          { duration: 0.2 },
        ]);
      }

      if (timelineSequence.length > 0 && typeof timeline9 !== "undefined") {
        timeline9(timelineSequence)
          .finished.then(() => {
            finishPanelTransition(
              drawer,
              panelToShow,
              linkListIndex,
              linkLists,
              panelIndex
            );
          })
          .catch(() => {
            finishPanelTransition(
              drawer,
              panelToShow,
              linkListIndex,
              linkLists,
              panelIndex
            );
          });
      } else {
        finishPanelTransition(
          drawer,
          panelToShow,
          linkListIndex,
          linkLists,
          panelIndex
        );
      }
    }

    function finishPanelTransition(
      drawer,
      panelToShow,
      linkListIndex,
      linkLists,
      panelIndex
    ) {
      if (
        drawer.previousLinkIndex !== undefined &&
        drawer.previousLinkIndex !== linkListIndex &&
        linkLists[drawer.previousLinkIndex]
      ) {
        linkLists[drawer.previousLinkIndex].removeAttribute("style");
      }

      drawer.previousLinkIndex = linkListIndex;
      panelToShow.setAttribute("open", "");
      drawer._isTransitioning = false;

      updateNavigationPathForPanel(drawer, panelIndex, linkListIndex);

      setTimeout(() => {
        if (drawer._navigationPath) {
          updateNavigationPath(drawer, drawer._navigationPath);
        }
      }, 100);
    }

    function restoreNormalNavigation(drawer) {
      const cache = getDOMCache(drawer);

      if (!cache.panels.length || !cache.panelWrapper) return;

      hidePanels(cache.panels, 1);

      const firstPanel = cache.panels[0];
      if (firstPanel) {
        firstPanel.style.display = "";
        firstPanel.style.opacity = "1";
        firstPanel.style.visibility = "visible";
        firstPanel.setAttribute("open", "");
      }

      cache.panelWrapper.classList.remove(...LEVEL_CLASSES);
      drawer.classList.remove(...LEVEL_CLASSES);

      if (!DESKTOP_MEDIA_QUERY.matches) {
        updateNavigationHeader(drawer, "Navigation", 0);
      }

      drawer.classList.remove("direct-level-2-mode");
      drawer._isDirectLevel2Mode = false;

      cache.panelWrapper.style.removeProperty("--navigation-drawer-width");

      if (cache.desktopBackLink) {
        cache.desktopBackLink.style.display = "none";
      }

      drawer._navigationPath = [];
      updateNavigationPath(drawer, []);
    }

    function updateNavigationHeader(
      drawer,
      title,
      panelLevel = 0,
      targetPanel = null
    ) {
      const cache = getDOMCache(drawer);

      if (panelLevel === 0) {
        if (cache.mobileLogo) {
          cache.mobileLogo.classList.remove("hidden");
        }
        if (cache.backButton) {
          cache.backButton.classList.remove("show");
        }
      } else {
        if (cache.mobileLogo) {
          cache.mobileLogo.classList.add("hidden");
        }
        if (cache.backButton && cache.backButtonTitle) {
          cache.backButton.classList.add("show");
          cache.backButtonTitle.textContent = title || "Navigation";

          if (targetPanel !== null) {
            cache.backButton.setAttribute("data-panel", targetPanel.toString());
          }
        }
      }
    }

    function hidePanels(panels, startIndex = 0, endIndex = null) {
      const end = endIndex || panels.length;
      for (let i = startIndex; i < end; i++) {
        const panel = panels[i];
        if (!panel) continue;

        panel.removeAttribute("open");
        panel.style.opacity = "0";
        panel.style.visibility = "hidden";

        const wrappers = panel.querySelectorAll(SELECTORS.PANEL_WRAPPER);
        wrappers.forEach((wrapper) => wrapper.setAttribute("hidden", ""));
      }
    }

    function setLevelClass(element, panelIndex) {
      element.classList.remove(...LEVEL_CLASSES);
      if (panelIndex >= 1 && panelIndex <= 3) {
        element.classList.add(`show-level-${panelIndex + 1}`);
      }
    }

    function updateNavigationPath(drawer, openPanelPath = []) {
      const allNavButtons = drawer.querySelectorAll(
        "[data-panel][aria-expanded]"
      );
      allNavButtons.forEach((button) => {
        button.setAttribute("aria-expanded", "false");
      });

      openPanelPath.forEach((panelId) => {
        const button = drawer.querySelector(
          `[data-panel="${panelId}"][aria-expanded]`
        );
        if (button) {
          button.setAttribute("aria-expanded", "true");
        }
      });
    }

    function updateNavigationPathForPanel(drawer, panelIndex, linkListIndex) {
      if (!drawer._navigationPath) {
        drawer._navigationPath = [];
      }

      const newPath = [...drawer._navigationPath];

      if (panelIndex >= 1) {
        if (drawer._isDirectLevel2Mode && drawer._navigationPath.length > 0) {
          newPath.length = 1;
        }

        if (panelIndex === 1 && linkListIndex !== null) {
          const panelId = `1-${linkListIndex + 1}`;
          const pathIndex = drawer._isDirectLevel2Mode ? 0 : 0;
          newPath.length = pathIndex + 1;
          newPath[pathIndex] = panelId;
        } else if (panelIndex === 2 && linkListIndex !== null) {
          const panelId = `2-${linkListIndex + 1}`;
          const pathIndex = drawer._isDirectLevel2Mode ? 1 : 1;
          newPath.length = pathIndex + 1;
          newPath[pathIndex] = panelId;
        } else if (panelIndex === 3 && linkListIndex !== null) {
          const panelId = `3-${linkListIndex + 1}`;
          const pathIndex = drawer._isDirectLevel2Mode ? 2 : 2;
          newPath.length = pathIndex + 1;
          newPath[pathIndex] = panelId;
        }

        drawer._navigationPath = newPath;
        updateNavigationPath(drawer, newPath);
      }
    }

    function addNavigationHeaderListeners() {
      const navigationPath = new Map();

      document.addEventListener(
        "click",
        function (event) {
          const button = event.target.closest("[data-panel]");
          if (!button) return;

          const drawer = button.closest("navigation-drawer");
          if (!drawer) return;

          const panelAttr = button.getAttribute("data-panel");
          if (!panelAttr) return;

          if (!drawer._navigationPath) {
            drawer._navigationPath = [];
          }

          const panelMatch = panelAttr.match(/^(\d+)-(\d+)$/);
          if (panelMatch) {
            const panelLevel = parseInt(panelMatch[1]);
            const panelIndex = parseInt(panelMatch[2]) - 1;

            const newPath = [...drawer._navigationPath];

            newPath.length = panelLevel;
            newPath[panelLevel - 1] = panelAttr;

            drawer._navigationPath = newPath;
            updateNavigationPath(drawer, newPath);

            setTimeout(() => updateNavigationPath(drawer, newPath), 50);
            setTimeout(() => updateNavigationPath(drawer, newPath), 200);
          }
        },
        true
      );

      document.addEventListener("click", function (event) {
        const backButton = event.target.closest(
          '[data-action="restore-normal-navigation"]'
        );
        if (!backButton) return;

        const drawer = backButton.closest("navigation-drawer");
        if (!drawer) return;

        restoreNormalNavigation(drawer);
      });

      function resetNavigationOnOpen() {
        const drawers = document.querySelectorAll("navigation-drawer");

        drawers.forEach((drawer) => {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.attributeName === "open") {
                const isOpen = drawer.hasAttribute("open");

                if (isOpen) {
                  // Load navigation images when drawer opens
                  loadNavigationImages(drawer);

                  // Skip reset if we're in the middle of direct level 2 opening OR already in direct level 2 mode
                  if (
                    drawer._directLevel2Opening ||
                    drawer._isDirectLevel2Mode
                  ) {
                    const reason = drawer._directLevel2Opening
                      ? "direct level 2 opening in progress"
                      : "already in direct level 2 mode";
                    return;
                  }

                  const drawerId = drawer.id || "default";

                  if (!DESKTOP_MEDIA_QUERY.matches) {
                    updateNavigationHeader(drawer, "Navigation", 0);
                  }

                  if (navigationPath.has(drawerId)) {
                    navigationPath.set(drawerId, ["Navigation"]);
                  }

                  if (drawer._navigationPath) {
                    drawer._navigationPath = [];
                    updateNavigationPath(drawer, []);
                  }

                  if (drawer._isDirectLevel2Mode) {
                    restoreNormalNavigation(drawer);
                  }
                }
              }
              // Invalidate cache if navigation structure changes
              else if (
                mutation.type === "childList" ||
                mutation.type === "subtree"
              ) {
                clearDOMCache(drawer);
              }
            });
          });

          observer.observe(drawer, {
            attributes: true,
            attributeFilter: ["open"],
            childList: true,
            subtree: true,
          });
        });
      }

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", resetNavigationOnOpen, {
          once: true,
        });
      } else {
        resetNavigationOnOpen();
      }

      document.addEventListener("click", function (event) {
        const button = event.target.closest("[data-panel][data-title]");
        if (!button) return;

        const drawer = button.closest("navigation-drawer");
        if (!drawer) return;

        const panelAttr = button.getAttribute("data-panel");
        const currentTitle = button.getAttribute("data-title");
        const drawerId = drawer.id || "default";

        if (panelAttr && currentTitle) {
          if (!navigationPath.has(drawerId)) {
            navigationPath.set(drawerId, ["Navigation"]);
          }

          const path = navigationPath.get(drawerId);

          let panelLevel = 0;
          let targetPanel = null;
          let headerTitle = "Navigation";

          if (panelAttr.startsWith("1-")) {
            panelLevel = 1;
            targetPanel = 0;
            headerTitle = "Navigation";
            path.length = 1;
            path.push(currentTitle);
          } else if (panelAttr.startsWith("2-")) {
            panelLevel = 2;
            targetPanel = 1;
            headerTitle = path[1] || "Navigation";
            path.length = 2;
            path.push(currentTitle);
          } else if (panelAttr.startsWith("3-")) {
            panelLevel = 3;
            targetPanel = 2;
            headerTitle = path[2] || "Navigation";
            path.length = 3;
            path.push(currentTitle);
          }

          setTimeout(() => {
            if (!DESKTOP_MEDIA_QUERY.matches) {
              updateNavigationHeader(
                drawer,
                headerTitle,
                panelLevel,
                targetPanel
              );
            }
          }, 50);
        }
      });

      document.addEventListener("click", function (event) {
        const backButton = event.target.closest(
          ".navigation-header__back-button"
        );
        if (!backButton) return;

        const drawer = backButton.closest("navigation-drawer");
        if (!drawer) return;

        const panelAttr = backButton.getAttribute("data-panel");
        const title = backButton.getAttribute("data-title");
        const drawerId = drawer.id || "default";

        if (title && panelAttr) {
          const targetLevel = parseInt(panelAttr);
          const path = navigationPath.get(drawerId) || ["Navigation"];

          setTimeout(() => {
            if (!DESKTOP_MEDIA_QUERY.matches) {
              if (targetLevel === 0) {
                updateNavigationHeader(drawer, "Navigation", 0);
                path.length = 1;

                if (drawer._navigationPath) {
                  drawer._navigationPath = [];
                  updateNavigationPath(drawer, []);
                }
              } else {
                path.length = targetLevel + 1;

                const headerTitle = path[targetLevel - 1] || "Navigation";

                let nextTargetPanel = targetLevel - 1;
                updateNavigationHeader(
                  drawer,
                  headerTitle,
                  targetLevel,
                  nextTargetPanel
                );

                if (
                  drawer._navigationPath &&
                  drawer._navigationPath.length > targetLevel
                ) {
                  drawer._navigationPath = drawer._navigationPath.slice(
                    0,
                    targetLevel
                  );
                  updateNavigationPath(drawer, drawer._navigationPath);
                }
              }
            }
          }, 50);
        }
      });
    }

    addNavigationHeaderListeners();

    // Complete initialization
    return Promise.resolve();
  }
})();
